import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";
import { getCustomerOrders, updateUserProfile } from "../api/axios";
import { toast } from "react-toastify";
import "../styles/ProfilePage.css";
import ForgotPassword from "./ForgotPasswordPage";

function ProfilePage() {
  const { user, setUser, logout } = useAuth();

  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    avatar: "/images/default-avatar.png",
  });

  /* ================= FETCH USER ORDERS ================= */
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const res = await getCustomerOrders();
        setTotalOrders(res?.data?.orders?.length || 0);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load your orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  /* ================= SYNC USER DATA ================= */
  useEffect(() => {
    if (!user) return;

    setFormData({
      name: user.name || "",
      phone: user.phone || "",
      avatar: user.avatar || "/images/default-avatar.png",
    });
  }, [user]);

  /* ================= AVATAR UPLOAD ================= */
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return toast.warning("Please upload a valid image");
    }

    if (file.size > 2 * 1024 * 1024) {
      return toast.warning("Image must be under 2MB");
    }

    try {
      setUploading(true);

      const data = new FormData();
      data.append("avatar", file);

      const res = await fetch("https://backend.sacmtb.com/api/upload/avatar", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        body: data,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message);

      const updatedUser = { ...user, avatar: result.imageUrl };

      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setFormData((p) => ({ ...p, avatar: result.imageUrl }));

      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ================= UPDATE PROFILE ================= */
  const handleUpdateProfile = async () => {
    if (!formData.name.trim()) {
      return toast.warning("Name cannot be empty");
    }

    if (!/^\d{10,15}$/.test(formData.phone)) {
      return toast.warning("Enter valid phone number");
    }

    try {
      setSaving(true);

      const res = await updateUserProfile({
        name: formData.name,
        phone: formData.phone,
      });

      const updatedUser = { ...user, ...res.data };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      toast.success("Profile updated successfully");
      setShowEditModal(false);
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  /* ================= LOADING / NO USER ================= */
  if (loading) {
    return (
      <div className="profile-page">
        <p className="loading">Loading profile...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <p className="loading">Session expired. Please login again.</p>
        <button className="action-btn" onClick={logout}>
          Go to Login
        </button>
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="sacProfilePage">

  {/* BACKGROUND */}

  <div className="sacProfileOverlay"></div>

  <img
    src="https://i.ibb.co/cS1d9nfp/sac-mtb-about-us-bicycle-nature.png"
    alt="SAC"
    className="sacProfileBg"
    loading="eager"
  />

  <div className="sacProfileGlow"></div>

  {/* PROFILE CARD */}

  <div className="sacProfileCard">

    {/* AVATAR */}

    <div className="sacAvatarWrap">

      <label htmlFor="avatar-upload">

        <img
          src={formData.avatar}
          alt="Avatar"
          className={`sacAvatar ${
            uploading
              ? "sacAvatarBlur"
              : ""
          }`}
        />

        <span className="sacAvatarLayer">

          {uploading
            ? "Uploading..."
            : "Change"}

        </span>

      </label>

      <input
        type="file"
        id="avatar-upload"
        hidden
        accept="image/*"
        onChange={handleImageUpload}
      />

    </div>

    {/* TITLE */}

    <h2 className="sacProfileHeading">

      SAC PROFILE

    </h2>

    {/* USER INFO */}

    <div className="sacInfoContainer">

      <div className="sacInfoCard">

        <span>
          NAME
        </span>

        <strong>
          {formData.name || "N/A"}
        </strong>

      </div>

      <div className="sacInfoCard">

        <span>
          EMAIL
        </span>

        <strong>
          {user.email}
        </strong>

      </div>

      <div className="sacInfoCard">

        <span>
          PHONE
        </span>

        <strong>
          {formData.phone || "N/A"}
        </strong>

      </div>

      <div className="sacInfoCard">

        <span>
          TOTAL ORDERS
        </span>

        <strong>
          {totalOrders}
        </strong>

      </div>

    </div>

    {/* ACTIONS */}

    <div className="sacActionWrap">

      <button
        className="sacActionBtn"
        onClick={() =>
          setShowEditModal(true)
        }
      >

        EDIT PROFILE

      </button>

      <button
        className="sacActionBtn sacSecondaryBtn"
        onClick={() =>
          setShowForgotPassword(true)
        }
      >

        RESET PASSWORD

      </button>

      <button
        className="sacActionBtn sacLogoutBtn"
        onClick={logout}
      >

        LOGOUT

      </button>

    </div>

  </div>

  {/* ================= EDIT MODAL ================= */}

  {showEditModal && (

    <div
      className="sacModalOverlay"
      onClick={() =>
        setShowEditModal(false)
      }
    >

      <div
        className="sacModalCard"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        <h3>

          EDIT PROFILE

        </h3>

        <input
          value={formData.name}
          onChange={(e) =>
            setFormData({
              ...formData,
              name:
                e.target.value,
            })
          }
          placeholder="Name"
        />

        <input
          value={formData.phone}
          onChange={(e) =>
            setFormData({
              ...formData,
              phone:
                e.target.value.replace(
                  /\D/g,
                  ""
                ),
            })
          }
          placeholder="Phone"
        />

        <div className="sacModalBtns">

          <button
            onClick={
              handleUpdateProfile
            }
            disabled={saving}
          >

            {saving
              ? "Saving..."
              : "Save"}

          </button>

          <button
            className="sacCancelBtn"
            onClick={() =>
              setShowEditModal(
                false
              )
            }
          >

            Cancel

          </button>

        </div>

      </div>

    </div>

  )}

  {/* ================= FORGOT PASSWORD ================= */}

  {showForgotPassword && (

    <div
      className="sacModalOverlay"
      onClick={() =>
        setShowForgotPassword(false)
      }
    >

      <div
        className="sacModalCard"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        <ForgotPassword />

      </div>

    </div>

  )}

</div>
  );
}

/* ================= REUSABLE COMPONENTS ================= */
const InfoRow = ({ label, value }) => (
  <div className="info-row">
    <span>{label}</span>
    <strong>{value || "N/A"}</strong>
  </div>
);

const Modal = ({ children, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

export default ProfilePage;
